import {Banner, Link} from '@primer/react'
import {useRef, useState} from 'react'

import {useDismissOrgNotice} from '../../hooks/use-dismiss-org-notice'
import {restoreFocusAfterDismiss} from '../../restore-focus-after-dismiss'
import type {IdentityManagementBannerPayload} from './types'

export const IAM_WITH_SAML_SSO_HELP_URL =
  'https://help.github.com/articles/about-identity-and-access-management-with-saml-single-sign-on'

/** Notice key the monolith uses to persist dismissal of the SAML SSO banner. */
const SAML_SSO_NOTICE = 'saml_sso_banner'

interface IdentityManagementBannerProps extends IdentityManagementBannerPayload {
  returnFocusIds?: string[]
}

/**
 * Informational banner shown to organization members once SAML single sign-on
 * has been enabled, prompting them to authenticate their account against the
 * organization's identity provider. Dismissing it persists the dismissal via
 * the org notice API so it stays hidden on subsequent visits.
 */
export function IdentityManagementBanner({
  organizationName,
  organizationLogin,
  organizationId,
  returnFocusIds,
}: IdentityManagementBannerProps) {
  const [dismissed, setDismissed] = useState(false)
  const bannerRef = useRef<HTMLElement>(null)
  const {dismissNotice} = useDismissOrgNotice(SAML_SSO_NOTICE, organizationLogin, String(organizationId))

  const onDismiss = async () => {
    try {
      if (await dismissNotice()) {
        restoreFocusAfterDismiss(bannerRef.current, returnFocusIds)
        setDismissed(true)
      }
    } catch {
      // Leave the banner in place so the viewer can retry.
    }
  }

  if (dismissed) return null

  return (
    <Banner
      ref={bannerRef}
      variant="info"
      title={`Single sign-on has been enabled for ${organizationName}.`}
      description={
        <>
          {`Single sign-on allows you to sign in to access this organization using your company's preferred identity provider. `}
          <Link inline href={IAM_WITH_SAML_SSO_HELP_URL}>
            Learn more about SAML single sign-on
          </Link>
        </>
      }
      onDismiss={onDismiss}
      flush
      primaryAction={
        <Banner.PrimaryAction as="a" href={`/orgs/${organizationLogin}/sso`}>
          Authenticate your account
        </Banner.PrimaryAction>
      }
    />
  )
}
